import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
} from '@chakra-ui/modal';
import { FaPencilAlt } from 'react-icons/fa';
import { dateToFrenchShort } from 'utils/date';

interface OrderDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    order: any;
    onEditOrder: (order: any) => void;
}

const OrderDetailModal = (props: OrderDetailModalProps) => {
    const { isOpen, onClose, order, onEditOrder } = props;

    if (!order) return null;

    const statusLabel =
        order.status === 'delivered'
            ? 'Livrée'
            : order.status === 'pending'
            ? 'En cours'
            : order.status;

    const statusClasses =
        order.status === 'delivered'
            ? 'bg-green-100 text-green-700'
            : order.status === 'pending'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-slate-100 text-slate-700';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
            <ModalContent className="!bg-white !rounded-2xl !shadow-2xl !mx-4 !my-4 sm:!my-8 !max-h-[80vh]">
                <ModalHeader className="!pb-2 !pt-4 !px-4 sm:!px-5 !pr-12 sm:!pr-14">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <h3 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">
                                Bon de livraison #{order.orderId}
                            </h3>
                            <p className="text-xs text-slate-500 mt-1">
                                Commandé le {dateToFrenchShort(order.createdAt)},{' '}
                                {order.status === 'delivered' ? 'livré le ' : 'livraison prévue le '}
                                {dateToFrenchShort(order.deliveryDate)}
                            </p>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${statusClasses}`}>
                            {statusLabel}
                        </div>
                    </div>
                </ModalHeader>
                <ModalCloseButton className="!top-3 !right-3 sm:!top-4 sm:!right-4" />

                <ModalBody className="!px-4 sm:!px-5 !pb-4 !overflow-y-auto">
                    <div className="space-y-1 mb-3">
                        {(order.items || []).map((item: any, index: number) => (
                            <div
                                key={index}
                                className="flex items-center justify-between py-2 px-2.5 bg-slate-50 rounded text-sm gap-2"
                            >
                                <span className="text-slate-700 flex-1 min-w-0">{item.name}</span>
                                <span className="text-slate-500 flex-shrink-0 whitespace-nowrap">
                                    {item.quantity} {item.unity}
                                </span>
                                <span className="text-slate-700 font-medium flex-shrink-0 w-16 text-right whitespace-nowrap">
                                    {((item.price || 0) * (item.quantity || 0)).toFixed(2)}€
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="pt-2 border-t border-slate-200 flex justify-between items-center">
                        <span className="text-sm text-slate-600">Total HT</span>
                        <span className="font-bold text-sm text-blue-600">
                            {(order.totalPrice || 0).toFixed(2)}€
                        </span>
                    </div>

                    {order.customerNotes && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                            <p className="text-xs text-slate-500 mb-0.5">Note de livraison :</p>
                            <p className="text-sm text-slate-700 italic break-words">{order.customerNotes}</p>
                        </div>
                    )}

                    {order.isEditable && (
                        <div className="mt-4 pt-3 border-t border-slate-200">
                            <button
                                onClick={() => onEditOrder(order)}
                                className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-sm font-semibold border border-amber-200 transition-colors"
                            >
                                <FaPencilAlt className="text-xs" />
                                Modifier cette commande
                            </button>
                        </div>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default OrderDetailModal;
